import { Site } from '@/types/search.types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchDocumentTags,
  fetchContentTags,
  fetchManagers,
  fetchAllOptions,
  fetchShowPaymentInstructions,
  updateShowPaymentInstructions,
} from './optionsThunks'

interface OptionsState {
  documentTags: string[]
  contentTags: string[]
  sites: Site[]
  managers: string[]
  showPaymentInstructions: boolean
  isLoading: boolean
  error: string | null
}

const initialState: OptionsState = {
  documentTags: [],
  contentTags: [],
  sites: [],
  managers: [],
  showPaymentInstructions: false,
  isLoading: false,
  error: null,
}

const optionsSlice = createSlice({
  name: 'options',
  initialState,
  reducers: {
    setDocumentTags: (state, action: PayloadAction<string[]>) => {
      state.documentTags = action.payload
      state.isLoading = false
      state.error = null
    },
    setContentTags: (state, action: PayloadAction<string[]>) => {
      state.contentTags = action.payload
      state.isLoading = false
      state.error = null
    },
    setSites: (state, action: PayloadAction<Site[]>) => {
      state.sites = action.payload
      state.isLoading = false
      state.error = null
    },
    setManagers: (state, action: PayloadAction<string[]>) => {
      state.managers = action.payload
      state.isLoading = false
      state.error = null
    },
    addDocumentTag: (state, action: PayloadAction<string>) => {
      if (!state.documentTags.includes(action.payload)) {
        state.documentTags.push(action.payload)
      }
    },
    addContentTag: (state, action: PayloadAction<string>) => {
      if (!state.contentTags.includes(action.payload)) {
        state.contentTags.push(action.payload)
      }
    },
    addSite: (state, action: PayloadAction<Site>) => {
      const exists = state.sites.some(site => site.id === action.payload.id)
      if (!exists) {
        state.sites.push(action.payload)
      }
    },
    addManager: (state, action: PayloadAction<string>) => {
      if (!state.managers.includes(action.payload)) {
        state.managers.push(action.payload)
      }
    },
    removeDocumentTag: (state, action: PayloadAction<string>) => {
      state.documentTags = state.documentTags.filter(tag => tag !== action.payload)
    },
    removeContentTag: (state, action: PayloadAction<string>) => {
      state.contentTags = state.contentTags.filter(tag => tag !== action.payload)
    },
    removeSite: (state, action: PayloadAction<string>) => {
      state.sites = state.sites.filter(site => site.id !== action.payload)
    },
    removeManager: (state, action: PayloadAction<string>) => {
      state.managers = state.managers.filter(manager => manager !== action.payload)
    },
    setShowPaymentInstructions: (state, action: PayloadAction<boolean>) => {
      state.showPaymentInstructions = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch Document Tags
    builder
      .addCase(fetchDocumentTags.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchDocumentTags.fulfilled, (state, action) => {
        state.documentTags = action.payload
        state.isLoading = false
      })
      .addCase(fetchDocumentTags.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Content Tags
    builder
      .addCase(fetchContentTags.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchContentTags.fulfilled, (state, action) => {
        state.contentTags = action.payload
        state.isLoading = false
      })
      .addCase(fetchContentTags.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Managers
    builder
      .addCase(fetchManagers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchManagers.fulfilled, (state, action) => {
        state.managers = action.payload
        state.isLoading = false
      })
      .addCase(fetchManagers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch All Options
    builder
      .addCase(fetchAllOptions.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAllOptions.fulfilled, (state, action) => {
        state.documentTags = action.payload.documentTags
        state.contentTags = action.payload.contentTags
        state.managers = action.payload.managers
        state.isLoading = false
      })
      .addCase(fetchAllOptions.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Show Payment Instructions
    builder
      .addCase(fetchShowPaymentInstructions.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchShowPaymentInstructions.fulfilled, (state, action) => {
        state.showPaymentInstructions = action.payload
        state.isLoading = false
      })
      .addCase(fetchShowPaymentInstructions.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Update Show Payment Instructions
    builder
      .addCase(updateShowPaymentInstructions.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateShowPaymentInstructions.fulfilled, (state, action) => {
        state.showPaymentInstructions = action.payload
        state.isLoading = false
      })
      .addCase(updateShowPaymentInstructions.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setDocumentTags,
  setContentTags,
  setSites,
  setManagers,
  addDocumentTag,
  addContentTag,
  addSite,
  addManager,
  removeDocumentTag,
  removeContentTag,
  removeSite,
  removeManager,
  setShowPaymentInstructions,
  setLoading,
  setError,
  clearError
} = optionsSlice.actions

export default optionsSlice.reducer
